import { PageLayout, PageLayoutProps } from '@backstage/frontend-plugin-api';
import { PluginHeader } from '@backstage/ui';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { useMemo } from 'react';
import { useResolvedPath } from 'react-router-dom';

const OmitPluginHeaderIfNotNecessary = (props: PageLayoutProps) => {
  // Omits the new PluginHeader component if the page to be shown
  // doesn't require it (=has header content).
  // The code is AI-generated, and meant as a temporary fix to hide
  // the plugin header during the transition period to NFS.
  // Should be removed when we are running fully on NFS.
  const { title, icon, titleLink, tabs, headerActions, children } = props;
  const parentPath = useResolvedPath('.').pathname.replace(/\/$/, '');

  const resolvedTabs = useMemo(
    () =>
      tabs?.map(tab => ({
        ...tab,
        href: tab.href.startsWith('/')
          ? tab.href
          : `${parentPath}/${tab.href}`.replace(/\/{2,}/g, '/'),
        matchStrategy: 'prefix' as const,
      })),
    [tabs, parentPath],
  );

  const pageHasHeaderContent = Boolean(
    resolvedTabs?.length || headerActions?.some(action => action !== null),
  );

  if (!pageHasHeaderContent) {
    // Safe to omit PluginHeader
    return <>{children}</>;
  }

  // Page has header content, use PluginHeader.
  return (
    <>
      <PluginHeader
        title={title}
        icon={icon}
        titleLink={titleLink}
        tabs={resolvedTabs}
        customActions={headerActions}
      />
      {children}
    </>
  );
};

const pageLayoutExtension = SwappableComponentBlueprint.make({
  name: 'page-layout',
  params: define =>
    define({
      component: PageLayout,
      loader: () => OmitPluginHeaderIfNotNecessary,
    }),
});

export const temporaryOmitPageHeaderModule = createFrontendModule({
  pluginId: 'app',
  extensions: [pageLayoutExtension],
});
